import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Platform, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import { useBankAccount } from '@/api/partners/bank-account/use-bank-account';
import { useCreateBankAccount } from '@/api/partners/bank-account/use-create-bank-account';
import { useUpdateBankAccount } from '@/api/partners/bank-account/use-update-bank-account';
import { isDesktop } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, ControlledInput, Text } from '@/ui';
import { CheckItem } from '@/ui/check-item';
import type { DropdownOption } from '@/ui/dropdown';
import Dropdown from '@/ui/dropdown';

const accountTypeOptions: DropdownOption[] = [
  {
    id: 'businessChecking',
    value: 'businessChecking',
    title: 'Business checking',
  },
  {
    id: 'businessSavings',
    value: 'businessSavings',
    title: 'Business savings',
  },
  {
    id: 'personalChecking',
    value: 'personalChecking',
    title: 'Personal checking',
  },
  {
    id: 'personalSavings',
    value: 'personalSavings',
    title: 'Personal savings',
  },
];

const countryOptions: DropdownOption[] = [
  { id: 'US', value: 'US', title: 'United States' },
];

// US bank account numbers are 4–17 digits.
const ACCOUNT_NUMBER_MAX_LENGTH = 17;

// Validates a US ABA routing number: 9 digits passing the standard checksum
// (3·(d1+d4+d7) + 7·(d2+d5+d8) + 1·(d3+d6+d9)) mod 10 === 0.
function isValidRoutingNumber(value: string): boolean {
  if (!/^\d{9}$/.test(value)) {
    return false;
  }

  const digits = value.split('').map(Number);
  const checksum =
    3 * (digits[0] + digits[3] + digits[6]) +
    7 * (digits[1] + digits[4] + digits[7]) +
    1 * (digits[2] + digits[5] + digits[8]);

  return checksum % 10 === 0;
}

const connectFormSchema = z
  .object({
    accountType: z.enum([
      'businessChecking',
      'businessSavings',
      'personalChecking',
      'personalSavings',
    ]),
    accountNumber: z
      .string()
      .regex(
        new RegExp(`^\\d{4,${ACCOUNT_NUMBER_MAX_LENGTH}}$`),
        'Enter a valid account number (4–17 digits)'
      ),
    confirmAccountNumber: z
      .string()
      .min(1, 'Please confirm your account number'),
    routingNumber: z
      .string()
      .regex(/^\d{9}$/, 'Routing number must be 9 digits')
      .refine(isValidRoutingNumber, 'Enter a valid routing number'),
    country: z.string().min(1, 'Country is required'),
    region: z.string().min(1, 'State / region is required'),
    city: z.string().min(1, 'City is required'),
    postalCode: z.string().min(1, 'Postal code is required'),
    address1: z.string().min(1, 'Street address is required'),
    address2: z.string().optional(),
    achAuthorized: z.boolean(),
  })
  .refine((data) => data.accountNumber === data.confirmAccountNumber, {
    message: 'Account numbers do not match',
    path: ['confirmAccountNumber'],
  })
  .refine((data) => data.achAuthorized === true, {
    message: 'You must authorize ACH payouts to continue',
    path: ['achAuthorized'],
  });

type ConnectFormType = z.infer<typeof connectFormSchema>;

export default function ConnectBankAccount() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const local = useLocalSearchParams<{ edit?: string; error?: string }>();
  const isEdit = local.edit === 'true';
  const hasActionRequiredError = local.error === 'true';

  const queryClient = useQueryClient();
  const { data: bankAccount } = useBankAccount({ enabled: isEdit });

  // A brand-new connect creates the Mercury recipient; an edit updates the
  // existing one in place. The screen picks the right mutation up front.
  const createMutation = useCreateBankAccount();
  const updateMutation = useUpdateBankAccount();
  const {
    mutateAsync: saveBankAccount,
    isPending,
    error,
  } = isEdit ? updateMutation : createMutation;

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    reset,
    watch,
    formState: { isValid },
  } = useForm<ConnectFormType>({
    mode: 'onChange',
    defaultValues: {
      accountType: 'businessChecking',
      accountNumber: '',
      confirmAccountNumber: '',
      routingNumber: '',
      country: 'US',
      region: '',
      city: '',
      postalCode: '',
      address1: '',
      address2: '',
      achAuthorized: false,
    },
    resolver: zodResolver(connectFormSchema),
  });

  const [accountType, country, achAuthorized] = watch([
    'accountType',
    'country',
    'achAuthorized',
  ]);

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Partner Bank Account Connect',
    });
  }, []);

  // In edit mode, prefill the account type and address. Account and routing
  // numbers are never returned by the backend, so they must be re-entered.
  useEffect(() => {
    if (isEdit && bankAccount?.status && bankAccount.status !== 'none') {
      reset({
        accountType: bankAccount.accountType ?? 'businessChecking',
        accountNumber: '',
        confirmAccountNumber: '',
        routingNumber: '',
        country: bankAccount.address?.country ?? 'US',
        region: bankAccount.address?.region ?? '',
        city: bankAccount.address?.city ?? '',
        postalCode: bankAccount.address?.postalCode ?? '',
        address1: bankAccount.address?.address1 ?? '',
        address2: bankAccount.address?.address2 ?? '',
        achAuthorized: false,
      });
    }
  }, [isEdit, bankAccount, reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      const result = await saveBankAccount({
        accountNumber: values.accountNumber,
        routingNumber: values.routingNumber,
        accountType: values.accountType,
        address1: values.address1,
        address2: values.address2 || undefined,
        city: values.city,
        region: values.region,
        postalCode: values.postalCode,
        country: values.country,
      });

      await queryClient.invalidateQueries({
        queryKey: ['partner/bank-account'],
      });

      // An address-only edit keeps the account verified, so only send the
      // partner into the verify flow when the account actually needs it.
      if (result?.status === 'pending_verification') {
        router.replace('/partner/more/bank-account/verify');
      } else {
        router.replace('/partner/more/bank-account');
      }
    } catch (e) {
      // Error surfaced via the `error` state below.
      console.error('Error connecting bank account:', e);
    }
  });

  const content = (
    <View style={styles.formContainer}>
      {Platform.OS === 'web' && isDesktop() ? (
        <View style={styles.desktopNav}>
          <Text type="title1">Bank Account</Text>
        </View>
      ) : null}

      <View style={{ gap: 16 }}>
        <Text type="bodyRegular">Account information</Text>

        <Dropdown
          mode="single"
          label="Account type"
          options={accountTypeOptions}
          placeHolderText="Select account type"
          onSelect={(selected: DropdownOption[]) => {
            setValue(
              'accountType',
              selected[0].value as ConnectFormType['accountType']
            );
            trigger('accountType');
          }}
          values={accountType ? [accountType] : []}
        />

        <ControlledInput
          control={control}
          name="accountNumber"
          label="Account number"
          placeholder="Enter account number"
          keyboardType="number-pad"
          maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
          secureTextEntry
          onBlur={() => trigger(['accountNumber', 'confirmAccountNumber'])}
        />

        <ControlledInput
          control={control}
          name="confirmAccountNumber"
          label="Confirm account number"
          placeholder="Confirm account number"
          keyboardType="number-pad"
          maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
          secureTextEntry
          onBlur={() => trigger('confirmAccountNumber')}
        />

        <ControlledInput
          control={control}
          name="routingNumber"
          label="Routing number"
          placeholder="9 digits"
          keyboardType="number-pad"
          maxLength={9}
          onBlur={() => trigger('routingNumber')}
        />
      </View>

      <View style={{ gap: 16 }}>
        <Text type="bodyRegular">Address</Text>

        <Dropdown
          mode="single"
          label="Country"
          options={countryOptions}
          placeHolderText="Select country"
          onSelect={(selected: DropdownOption[]) => {
            setValue('country', selected[0].value);
            trigger('country');
          }}
          values={country ? [country] : []}
        />

        <ControlledInput
          control={control}
          name="region"
          label="State / Region"
          placeholder="State or region"
          onBlur={() => trigger('region')}
        />

        <ControlledInput
          control={control}
          name="city"
          label="City"
          placeholder="City"
          onBlur={() => trigger('city')}
        />

        <ControlledInput
          control={control}
          name="postalCode"
          label="Postal code"
          placeholder="ZIP or postal code"
          onBlur={() => trigger('postalCode')}
        />

        <ControlledInput
          control={control}
          name="address1"
          label="Street address"
          placeholder="Street address"
          onBlur={() => trigger('address1')}
        />

        <ControlledInput
          control={control}
          name="address2"
          label="Apt, suite, unit (optional)"
          placeholder="Apt, suite, unit"
        />
      </View>

      <CheckItem
        type="checkbox"
        active={achAuthorized}
        onPress={() => {
          setValue('achAuthorized', !achAuthorized);
          trigger('achAuthorized');
        }}
        text="I authorize Eon and its banking partner to send payouts to this account via ACH"
        caption="I confirm I'm the account holder or authorized to act on their behalf, and that this information is accurate."
      />

      <View style={styles.buttonDesktop}>
        <Button
          text="Save"
          type="primary"
          onPress={onSubmit}
          loading={isPending}
          disabled={!isValid || isPending}
          style={{ width: 120 }}
        />
      </View>

      {error || hasActionRequiredError ? (
        <Text type="caption1" style={{ color: theme.colors.error }}>
          {error
            ? error.message
            : 'There was an issue with your bank account. Please contact support at support@eonrides.com.'}
        </Text>
      ) : null}
    </View>
  );

  return (
    <View style={styles.container}>
      <KeyboardAwareScrollView
        style={styles.scrollView}
        contentContainerStyle={{ paddingBottom: 100 }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {content}
      </KeyboardAwareScrollView>

      <View style={styles.buttonMobile}>
        <Button
          text={isEdit ? 'Save Changes' : 'Save'}
          type="primary"
          onPress={onSubmit}
          loading={isPending}
          disabled={!isValid || isPending}
        />
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
  scrollView: {
    flexGrow: 1,
    paddingHorizontal: {
      xs: 16,
      md: 56,
    },
    paddingTop: {
      xs: 16,
      md: 56,
    },
  },
  formContainer: {
    gap: 32,
    alignSelf: 'center',
    width: {
      xs: '100%',
      md: 600,
    },
  },
  desktopNav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    gap: 16,
  },
  buttonMobile: {
    display: {
      xs: 'flex',
      md: 'none',
    },
    padding: 16,
  },
  buttonDesktop: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    alignItems: {
      md: 'flex-end',
    },
  },
}));
